import React, { useEffect, useState } from 'react';
import { getMenu, createOrder } from '@/services/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ShoppingCart, Plus, Minus, Check } from 'lucide-react';
import { toast } from 'sonner';

const KioskApp = () => {
  const [menu, setMenu] = useState([]);
  const [cart, setCart] = useState([]);
  const [storeId] = useState('a38ac377-25a5-40e9-9e28-f919fb1adeee');
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('All');
  
  // Checkout flow
  const [showCheckout, setShowCheckout] = useState(false);
  const [orderType, setOrderType] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [orderNumber, setOrderNumber] = useState(null);
  const [inactivityTimer, setInactivityTimer] = useState(null);

  useEffect(() => {
    loadMenu();
    resetInactivityTimer();
    
    // Reset on any interaction
    const handleActivity = () => resetInactivityTimer();
    window.addEventListener('touchstart', handleActivity);
    window.addEventListener('click', handleActivity);
    
    return () => {
      window.removeEventListener('touchstart', handleActivity);
      window.removeEventListener('click', handleActivity);
      if (inactivityTimer) clearTimeout(inactivityTimer);
    };
  }, []);

  const resetInactivityTimer = () => {
    if (inactivityTimer) clearTimeout(inactivityTimer);
    
    const timer = setTimeout(() => {
      // Auto-reset after 2 minutes of inactivity
      if (cart.length > 0 || showCheckout || showConfirmation) {
        resetKiosk();
        toast.info('Session reset due to inactivity');
      }
    }, 120000); // 2 minutes
    
    setInactivityTimer(timer);
  };

  const resetKiosk = () => {
    setCart([]);
    setShowCheckout(false);
    setShowConfirmation(false);
    setOrderType('');
    setCustomerName('');
    setCustomerPhone('');
    setSelectedCategory('All');
  };

  const loadMenu = async () => {
    try {
      const response = await getMenu(storeId);
      const menuItems = response.data;
      setMenu(menuItems);
      
      // Extract unique categories
      const cats = ['All', ...new Set(menuItems.map(item => item.category))];
      setCategories(cats);
    } catch (error) {
      console.error('Error loading menu:', error);
    }
  };

  const addToCart = (item) => {
    const existing = cart.find(c => c.id === item.id);
    if (existing) {
      setCart(cart.map(c => c.id === item.id ? { ...c, qty: c.qty + 1 } : c));
    } else {
      setCart([...cart, { ...item, qty: 1 }]);
    }
    toast.success(`${item.name} added to cart`);
  };

  const updateQty = (itemId, delta) => {
    setCart(cart.map(c => {
      if (c.id === itemId) {
        const newQty = c.qty + delta;
        return newQty > 0 ? { ...c, qty: newQty } : null;
      }
      return c;
    }).filter(Boolean));
  };

  const getSubtotal = () => cart.reduce((sum, item) => sum + (item.price * item.qty), 0);
  const getTax = () => getSubtotal() * 0.05; // 5% tax for kiosk orders
  const getTotal = () => getSubtotal() + getTax();

  const filteredMenu = selectedCategory === 'All' 
    ? menu 
    : menu.filter(item => item.category === selectedCategory);

  const proceedToCheckout = () => {
    if (cart.length === 0) {
      toast.error('Cart is empty');
      return;
    }
    setShowCheckout(true);
  };

  const handlePlaceOrder = async () => {
    if (!orderType) {
      toast.error('Please select order type');
      return;
    }

    try {
      const orderData = {
        store_id: storeId,
        order_type: orderType,
        source: 'kiosk',
        items: cart.map(item => ({
          menu_item_id: item.id,
          menu_item_name: item.name,
          qty: item.qty,
          price: item.price,
          notes: ''
        })),
        customer: customerName || customerPhone ? {
          name: customerName,
          phone: customerPhone,
          address: ''
        } : null,
        discount: 0
      };

      const response = await createOrder(orderData);
      const order = response.data;
      
      setOrderNumber(order.order_number);
      setShowCheckout(false);
      setShowConfirmation(true);
      
      // Auto-reset after showing confirmation
      setTimeout(() => {
        resetKiosk();
      }, 5000);
    } catch (error) {
      console.error('Order error:', error);
      toast.error('Failed to place order');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-red-50 to-pink-50">
      {/* Header */}
      <div className="bg-white shadow-md py-6 px-4 text-center sticky top-0 z-10">
        <h1 className="text-4xl md:text-5xl font-bold text-orange-600 mb-1">🍕 SSPICE PIZZA</h1>
        <p className="text-lg text-gray-600">Self-Service Ordering</p>
      </div>

      <div className="container mx-auto px-4 py-6 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Menu Section */}
          <div className="lg:col-span-2">
            {/* Categories */}
            <div className="flex overflow-x-auto gap-2 mb-6 pb-2">
              {categories.map((cat) => (
                <Button
                  key={cat}
                  variant={selectedCategory === cat ? 'default' : 'outline'}
                  className={`whitespace-nowrap ${selectedCategory === cat ? 'bg-orange-600' : ''}`}
                  onClick={() => setSelectedCategory(cat)}
                >
                  {cat}
                </Button>
              ))}
            </div>

            {/* Menu Items */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {filteredMenu.map((item) => (
                <Card
                  key={item.id}
                  data-testid={`kiosk-item-${item.sku}`}
                  className="overflow-hidden hover:shadow-2xl transition-all cursor-pointer transform hover:scale-105 active:scale-95"
                  onClick={() => addToCart(item)}
                >
                  <div className="aspect-square bg-gradient-to-br from-orange-300 to-red-400 flex items-center justify-center">
                    <span className="text-7xl">🍕</span>
                  </div>
                  <CardContent className="p-4">
                    <h3 className="font-bold text-base md:text-lg mb-1 line-clamp-2">{item.name}</h3>
                    <p className="text-sm text-gray-600 mb-2">{item.category}</p>
                    <p className="text-2xl md:text-3xl font-bold text-orange-600">₹{item.price}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* Cart Section */}
          <div className="lg:col-span-1">
            <Card data-testid="kiosk-cart" className="sticky top-24 shadow-2xl">
              <CardContent className="p-6">
                <div className="flex items-center mb-4">
                  <ShoppingCart className="w-6 h-6 mr-2 text-orange-600" />
                  <h2 className="text-2xl font-bold">Your Order</h2>
                </div>

                {cart.length > 0 ? (
                  <>
                    <div className="space-y-3 mb-6 max-h-80 overflow-y-auto">
                      {cart.map((item) => (
                        <div key={item.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div className="flex-1 pr-2">
                            <p className="font-medium text-sm md:text-base">{item.name}</p>
                            <p className="text-sm text-gray-500">₹{item.price} x {item.qty}</p>
                          </div>
                          <div className="flex items-center space-x-2">
                            <Button 
                              size="sm" 
                              variant="outline" 
                              className="h-8 w-8 p-0" 
                              onClick={(e) => { e.stopPropagation(); updateQty(item.id, -1); }}
                            >
                              <Minus className="w-4 h-4" />
                            </Button>
                            <span className="w-8 text-center font-bold">{item.qty}</span>
                            <Button 
                              size="sm" 
                              variant="outline" 
                              className="h-8 w-8 p-0" 
                              onClick={(e) => { e.stopPropagation(); updateQty(item.id, 1); }}
                            >
                              <Plus className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="border-t pt-4 mb-6 space-y-2">
                      <div className="flex justify-between text-base">
                        <span>Subtotal:</span>
                        <span>₹{getSubtotal().toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-base">
                        <span>Tax (5%):</span>
                        <span>₹{getTax().toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-2xl font-bold text-orange-600">
                        <span>Total:</span>
                        <span>₹{getTotal().toFixed(2)}</span>
                      </div>
                    </div>

                    <Button
                      data-testid="place-order-button"
                      className="w-full bg-orange-600 hover:bg-orange-700 text-xl py-6"
                      onClick={proceedToCheckout}
                    >
                      Proceed to Order
                    </Button>
                    
                    <Button
                      variant="outline"
                      className="w-full mt-2"
                      onClick={() => setCart([])}
                    >
                      Clear Cart
                    </Button>
                  </>
                ) : (
                  <div className="text-center py-12 text-gray-500">
                    <ShoppingCart className="w-16 h-16 mx-auto mb-4 opacity-30" />
                    <p className="text-lg">Your cart is empty</p>
                    <p className="text-sm">Start adding items!</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Checkout Modal */}
      <Dialog open={showCheckout} onOpenChange={setShowCheckout}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl">Complete Your Order</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-base mb-2 block">Order Type *</Label>
              <div className="grid grid-cols-2 gap-3">
                <Button
                  variant={orderType === 'DINE_IN' ? 'default' : 'outline'}
                  className={`h-16 ${orderType === 'DINE_IN' ? 'bg-orange-600' : ''}`}
                  onClick={() => setOrderType('DINE_IN')}
                >
                  🍽️<br />Dine In
                </Button>
                <Button
                  variant={orderType === 'TAKEAWAY' ? 'default' : 'outline'}
                  className={`h-16 ${orderType === 'TAKEAWAY' ? 'bg-orange-600' : ''}`}
                  onClick={() => setOrderType('TAKEAWAY')}
                >
                  🥡<br />Takeaway
                </Button>
              </div>
            </div>
            
            <div>
              <Label htmlFor="kiosk-name" className="text-base">Name (Optional)</Label>
              <Input
                id="kiosk-name"
                placeholder="Your name"
                value={customerName}
                onChange={(e) => setCustomerName(e.target.value)}
                className="text-lg h-12"
              />
            </div>
            
            <div>
              <Label htmlFor="kiosk-phone" className="text-base">Phone (Optional)</Label>
              <Input
                id="kiosk-phone"
                type="tel"
                inputMode="numeric"
                placeholder="10-digit phone"
                value={customerPhone}
                onChange={(e) => setCustomerPhone(e.target.value)}
                maxLength={10}
                className="text-lg h-12"
              />
            </div>

            <div className="bg-orange-50 p-4 rounded-lg">
              <div className="flex justify-between text-2xl font-bold text-orange-600">
                <span>Total to Pay:</span>
                <span>₹{getTotal().toFixed(2)}</span>
              </div>
              <p className="text-sm text-gray-600 mt-2">Please proceed to counter for payment</p>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowCheckout(false)} className="flex-1">
              Back
            </Button>
            <Button 
              className="bg-orange-600 hover:bg-orange-700 flex-1"
              onClick={handlePlaceOrder}
            >
              <Check className="w-5 h-5 mr-2" />
              Place Order
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirmation Modal */}
      <Dialog open={showConfirmation} onOpenChange={setShowConfirmation}>
        <DialogContent className="max-w-md text-center">
          <div className="py-8">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check className="w-12 h-12 text-green-600" />
            </div>
            <h2 className="text-3xl font-bold text-gray-800 mb-2">Order Placed!</h2>
            <div className="bg-orange-50 p-6 rounded-lg my-6">
              <p className="text-sm text-gray-600 mb-2">Your Order Number</p>
              <p className="text-6xl font-bold text-orange-600">#{orderNumber}</p>
            </div>
            <p className="text-lg text-gray-600 mb-4">Please proceed to the counter for payment</p>
            <p className="text-sm text-gray-500">This screen will reset automatically...</p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default KioskApp;